import {Signal} from '@angular/core';
import {PolarisAccountKey, PolarisUserId} from './polaris-api.models';
import {PolarisFacadeContract} from './polaris-facade-view.models';

export type PolarisRuntimeMode = 'legacy' | 'new';

export type PolarisRuntimeModeSource =
  | 'migration_marker'
  | 'migration_success'
  | 'migration_failure'
  | 'default_legacy'
  | 'manual_override';

export interface PolarisRuntimeModeSelection {
  mode: PolarisRuntimeMode;
  source: PolarisRuntimeModeSource;
  selectedAt: number;
  reason?: string;
}

export interface PolarisMigrationScope {
  accountKey: PolarisAccountKey;
  userId: PolarisUserId;
}

export enum PolarisEnvId {
  PRODUCTION = 'env-1',
  QA = 'env-2',
  LOCAL = 'env-3'
}

export interface PolarisMigrationSuccessMarker {
  status: 'success';
  completedAt: number;
  version: number;
  environment: PolarisEnvId;
}

export interface PolarisMigrationAttemptSuccessSummary {
  status: 'success';
  totalChatCount: number;
  uploadedChatCount: number;
  attemptCount: number;
}

export type PolarisMigrationErrorCategory =
  | 'forbidden'
  | 'server'
  | 'network'
  | 'validation'
  | 'unknown';

export interface PolarisMigrationAttemptFailureSummary {
  status: 'failed';
  totalChatCount: number;
  uploadedChatCount: 0;
  attemptCount: number;
  errorCategory: PolarisMigrationErrorCategory;
  statusCode?: number;
  retryExhausted: boolean;
  errorMessage: string;
}

export type PolarisMigrationAttemptSummary =
  | PolarisMigrationAttemptSuccessSummary
  | PolarisMigrationAttemptFailureSummary;

export interface PolarisRuntimeInitResult {
  modeSelection: PolarisRuntimeModeSelection;
  migrationAttempt?: PolarisMigrationAttemptSummary;
}

export interface PolarisMigrationOrchestratorContract {
  initializeRuntime(scope: PolarisMigrationScope): Promise<PolarisRuntimeInitResult>;
  getSuccessMarker(scope: PolarisMigrationScope): PolarisMigrationSuccessMarker | null;
  setSuccessMarker(scope: PolarisMigrationScope, marker: PolarisMigrationSuccessMarker): void;
}

export interface PolarisRuntimeFacadeContract extends PolarisFacadeContract {
  readonly runtimeMode: Signal<PolarisRuntimeMode>;
  readonly isUsingLegacyFlow: Signal<boolean>;
  initPolarisRuntime(scope: PolarisMigrationScope): Promise<PolarisRuntimeInitResult>;
}
